import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Paper, Skeleton, Typography } from "@mui/material";
import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";
import { IFlyingCart } from "../../../IFlyingCart";
import { FlyingCartContent } from "../FlyingCartContent/FlyingCartContent";
import { useStyle } from "./style";
import { FlyingCartStepperButton } from "../FlyingCartFooter/FlyingCartStepperButton";
import { IStepRoute } from "@common/StepperRouter";
import DownArrow from "@static/icons/DownArrow.svg";

interface IParams {
    arePricesLoading?: boolean;
    flyingCart: IFlyingCart;
    buttonType?: "button" | "submit";
    nextStep?: IStepRoute;
    onNextClick?(): void;
}

export const FlyingCartMobile = (props: IParams) => {
    const { t } = useTranslation(["cart", "common"]);

    const { flyingCart, onNextClick, buttonType, nextStep, arePricesLoading } = props;

    const [isExpanded, setIsExpanded] = useState(false);
    const { classes } = useStyle({ isExpanded, arePricesLoading });

    const handleToggleExpanded = useCallback(() => {
        setIsExpanded((prev) => !prev);
    }, []);

    return (
        <>
            {isExpanded && <Box className={classes.flyingCartMobileOverlay} onClick={() => setIsExpanded(false)} />}
            {/* Toggle button - floating above the container */}
            <IconButton className={classes.flyingCartMobileToggleButton} size="small" onClick={handleToggleExpanded}>
                <DownArrow
                    className={`${classes.flyingCartMobileArrow} ${
                        isExpanded ? "" : classes.flyingCartMobileArrowRotated
                    }`}
                    height={18}
                    width={18}
                />
            </IconButton>
            {/* Main container */}
            <Paper className={classes.flyingCartMobileContainer} elevation={2}>
                {isExpanded && (
                    <div className={classes.flyingCartMobileExpandedContent}>
                        <FlyingCartContent arePricesLoading={arePricesLoading} flyingCart={flyingCart} />
                    </div>
                )}
                <div className={classes.flyingCartMobileFooter}>
                    <div className={classes.flyingCartMobileFooterInfo}>
                        <Typography className={classes.flyingCartMobileFooterTitle} variant="h6">
                            {t("cart:total")}:
                        </Typography>
                        <div className={classes.flyingCartMobileFooterGrid}>
                            <div className={classes.flyingCartMobileFooterToday}>
                                <Typography lineHeight="16px" variant="small">
                                    {t("cart:today")}:{" "}
                                    <HandleErrorAndLoading
                                        isLoading={arePricesLoading ?? false}
                                        skeleton={
                                            <Skeleton
                                                className={classes.flyingCartMobilePriceSkeleton}
                                                variant="text"
                                            />
                                        }
                                    >
                                        {t("common:price", { price: flyingCart.totalUpFrontAmount })}
                                    </HandleErrorAndLoading>
                                </Typography>
                            </div>
                            <div className={classes.flyingCartMobileFooterMonthly}>
                                <Typography lineHeight="16px" variant="small">
                                    {t("cart:monthly")}:{" "}
                                    <HandleErrorAndLoading
                                        isLoading={arePricesLoading ?? false}
                                        skeleton={
                                            <Skeleton
                                                className={classes.flyingCartMobilePriceSkeleton}
                                                variant="text"
                                            />
                                        }
                                    >
                                        {t("common:price", { price: flyingCart.totalRecurringAmount })}
                                    </HandleErrorAndLoading>
                                </Typography>
                            </div>
                        </div>
                    </div>
                    {onNextClick && (
                        <FlyingCartStepperButton
                            buttonType={buttonType}
                            isMobile
                            nextStep={nextStep}
                            onNextClick={onNextClick}
                        />
                    )}
                </div>
            </Paper>
        </>
    );
};
